import React, { useState, useEffect } from 'react';
import { Users, CheckSquare, ShieldCheck, Layers, AlertTriangle, Scan, TrendingUp, Activity } from 'lucide-react';
import {
  LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Avatar from '../../components/ui/Avatar';
import api from '../../services/api';

function KpiCard({ icon: Icon, label, value, sub, color, bg }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 16, padding: '20px',
      border: '1px solid #E2E8F0', display: 'flex', gap: 14, alignItems: 'flex-start',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      transition: 'all 0.2s ease',
    }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.06)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
      <div style={{
        width: 46, height: 46, borderRadius: 12, background: bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Icon size={21} color={color} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 24, fontWeight: 800, color: '#0F172A', lineHeight: 1.2, letterSpacing: '-0.02em' }}>{value}</div>
        <div style={{ fontSize: 13, color: '#64748B', marginTop: 2 }}>{label}</div>
        {sub && <div style={{ fontSize: 12, color: color, fontWeight: 600, marginTop: 4 }}>{sub}</div>}
      </div>
    </div>
  );
}

const actionTypeLabels = {
  scan: 'Сканирование',
  osg_check: 'ОСГ проверка',
  display_complete: 'Выкладка',
  photo_upload: 'Фото-отчёт',
  task_accept: 'Принята задача',
  task_cancel: 'Отмена задачи',
};

const actionTypeBadge = {
  scan: 'primary',
  osg_check: 'osg',
  display_complete: 'display',
  photo_upload: 'success',
  task_accept: 'in_progress',
  task_cancel: 'error',
};

export default function Dashboard() {
  const [period, setPeriod] = useState('today');
  const [dashboard, setDashboard] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [actionLogs, setActionLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [dashRes, dailyRes, taskTypesRes, logsRes] = await Promise.allSettled([
          api.get('/v1/analytics/dashboard'),
          api.get('/v1/analytics/daily'),
          api.get('/v1/analytics/task-types'),
          api.get('/v1/employees/actions?page=1&per_page=10'),
        ]);
        if (dashRes.status === 'fulfilled' && dashRes.value?.data) setDashboard(dashRes.value.data);
        if (dailyRes.status === 'fulfilled' && dailyRes.value?.data) setDailyData(dailyRes.value.data || []);
        if (taskTypesRes.status === 'fulfilled' && taskTypesRes.value?.data) setTaskTypes(taskTypesRes.value.data || []);
        if (logsRes.status === 'fulfilled' && logsRes.value?.data) setActionLogs(logsRes.value.data || []);
      } catch (e) {
        console.error('Dashboard load error', e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [period]);

  const stats = dashboard || {};
  const today = new Date().toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em' }}>Дашборд</h1>
          <p style={{ color: '#94A3B8', fontSize: 13, marginTop: 3, fontWeight: 500 }}>Обзор состояния сети &bull; {today}</p>
        </div>
        <div style={{ display: 'flex', gap: 4, background: '#F1F5F9', borderRadius: 10, padding: 3 }}>
          {['today', 'week', 'month'].map(p => (
            <button key={p} onClick={() => setPeriod(p)} style={{
              padding: '7px 16px', borderRadius: 8, border: 'none',
              background: period === p ? 'linear-gradient(135deg, #00CEEE, #009995)' : 'transparent',
              color: period === p ? '#fff' : '#64748B',
              fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              transition: 'all 0.2s',
              boxShadow: period === p ? '0 2px 8px rgba(0,206,238,0.25)' : 'none',
            }}>
              {{ today: 'Сегодня', week: 'Неделя', month: 'Месяц' }[p]}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            border: '3px solid rgba(0,206,238,0.15)',
            borderTopColor: '#00CEEE',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 12px',
          }} />
          <span style={{ color: '#94A3B8', fontSize: 13, fontWeight: 500 }}>Загрузка данных...</span>
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
            <KpiCard icon={Users} label="Сотрудников / Онлайн" value={`${stats.total_employees || 0} / ${stats.online_now || 0}`} sub={`${stats.online_now || 0} онлайн`} color="#00CEEE" bg="#E6FAFB" />
            <KpiCard icon={CheckSquare} label="Задач / Выполнено" value={`${stats.total_tasks || 0} / ${stats.completed_tasks || 0}`} sub={`${stats.total_tasks > 0 ? Math.round((stats.completed_tasks || 0) / stats.total_tasks * 100) : 0}% выполнено`} color="#10B981" bg="#ECFDF5" />
            <KpiCard icon={ShieldCheck} label="ОСГ проверки" value={stats.osg_checks || 0} sub="всего проверок" color="#EF4444" bg="#FEF2F2" />
            <KpiCard icon={Layers} label="Выкладка" value={stats.display_reports || 0} sub="отчётов" color="#F59E0B" bg="#FFFBEB" />
            <KpiCard icon={AlertTriangle} label="Критический срок" value={stats.critical_expiry || 0} sub="товаров" color="#EA580C" bg="#FFF7ED" />
            <KpiCard icon={Scan} label="Сканирований" value={stats.total_scans || 0} sub="всего" color="#8B5CF6" bg="#F5F3FF" />
          </div>

          {/* Charts row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 20 }}>
            <Card title="Активность" extra={<TrendingUp size={16} color="#00CEEE" />}>
              {dailyData.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={dailyData.slice(-14)} margin={{ top: 5, right: 10, bottom: 5, left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94A3B8' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 10, border: '1px solid #E2E8F0', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }} />
                    <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="scans" stroke="#00CEEE" strokeWidth={2.5} dot={false} name="Сканирования" />
                    <Line type="monotone" dataKey="osg_checks" stroke="#EF4444" strokeWidth={2.5} dot={false} name="ОСГ проверки" />
                    <Line type="monotone" dataKey="displays" stroke="#F59E0B" strokeWidth={2.5} dot={false} name="Выкладки" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ textAlign: 'center', padding: 50, color: '#94A3B8', fontSize: 13 }}>Нет данных за период</div>
              )}
            </Card>

            <Card title="Типы задач" extra={<Activity size={16} color="#00CEEE" />}>
              {taskTypes.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={taskTypes} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                      paddingAngle={3} dataKey="value" nameKey="name">
                      {taskTypes.map((e, i) => <Cell key={i} fill={e.fill || ['#00CEEE', '#009995', '#F59E0B', '#EF4444', '#8B5CF6'][i % 5]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 10, border: '1px solid #E2E8F0' }} />
                    <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ textAlign: 'center', padding: 50, color: '#94A3B8', fontSize: 13 }}>Нет данных</div>
              )}
            </Card>
          </div>

          {/* Live feed */}
          <Card title="Последние действия" extra={
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10B981', animation: 'pulse 2s infinite' }} />
              <span style={{ fontSize: 12, color: '#10B981', fontWeight: 600 }}>Live</span>
            </div>
          }>
            {actionLogs.length > 0 ? (
              <div style={{ overflowX: 'auto', margin: '-20px', WebkitOverflowScrolling: 'touch' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 600 }}>
                  <thead>
                    <tr style={{ background: '#F8FAFC' }}>
                      {['Время', 'Сотрудник', 'Действие', 'Описание', 'Статус'].map(h => (
                        <th key={h} style={{
                          padding: '10px 16px', textAlign: 'left', fontSize: 11,
                          fontWeight: 600, color: '#64748B', borderBottom: '1px solid #E2E8F0',
                          textTransform: 'uppercase', letterSpacing: '0.04em',
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {actionLogs.map(log => (
                      <tr key={log.id} style={{ borderBottom: '1px solid #F1F5F9', transition: 'background 0.1s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#F8FAFB'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '12px 16px', color: '#94A3B8', whiteSpace: 'nowrap', fontSize: 12 }}>
                          {log.created_at ? new Date(log.created_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '\u2014'}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Avatar name={log.employee_name || '?'} size={28} />
                            <span style={{ fontWeight: 500, fontSize: 13 }}>{log.employee_name || '\u2014'}</span>
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <Badge variant={actionTypeBadge[log.type] || 'gray'}>{actionTypeLabels[log.type] || log.type || '\u2014'}</Badge>
                        </td>
                        <td style={{ padding: '12px 16px', color: '#334155', maxWidth: 250 }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.description || '\u2014'}</div>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <Badge variant={log.result === 'success' ? 'success' : log.result === 'error' ? 'error' : 'gray'}>
                            {log.result === 'success' ? 'Успешно' : log.result === 'error' ? 'Ошибка' : log.result || '\u2014'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 50, color: '#94A3B8', fontSize: 13 }}>Нет действий</div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
